<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4" style="background-color: rgba(0, 0, 0, 0.65); backdrop-filter: blur(2px);">
    <div class="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 text-left animate-fadeIn">
      
      <!-- Header -->
      <div style="background-color: #1e293b;" class="px-6 py-4 flex items-center justify-between text-white">
        <div class="flex items-center space-x-3">
          <i class="fa fa-hand-holding-usd text-2xl text-red-400"></i>
          <div>
            <h3 class="font-bold text-base md:text-lg leading-tight text-white">Retiro de Efectivo de Caja</h3>
            <p class="text-xs text-gray-300 mt-0.5">Egreso protegido por autorización de Administrador</p>
          </div>
        </div>
        <button
          type="button"
          @click="close"
          class="text-gray-400 hover:text-white text-2xl font-bold p-1 leading-none transition-colors"
        >
          &times;
        </button>
      </div>

      <!-- Admin Tabs (si es admin) -->
      <div v-if="isAdminUser" class="flex border-b border-gray-200 bg-gray-50 text-xs font-bold">
        <button
          type="button"
          @click="tab = 'withdrawal'"
          class="flex-1 py-3 text-center transition-colors cursor-pointer"
          :style="tab === 'withdrawal' ? 'border-bottom: 2px solid #00b074; color: #00b074; background-color: #ffffff;' : 'color: #6b7280;'"
        >
          <i class="fa fa-money-bill-wave mr-1"></i> Registrar Retiro
        </button>
        <button
          type="button"
          @click="tab = 'pin_config'"
          class="flex-1 py-3 text-center transition-colors cursor-pointer"
          :style="tab === 'pin_config' ? 'border-bottom: 2px solid #00b074; color: #00b074; background-color: #ffffff;' : 'color: #6b7280;'"
        >
          <i class="fa fa-key mr-1"></i> Configurar Clave de Retiros
        </button>
      </div>

      <!-- Tab 1: Registrar Retiro -->
      <div v-if="tab === 'withdrawal'" class="p-6">
        <form @submit.prevent="submitWithdrawal">

          <!-- Selector de Caja Destino (Para Administradores) -->
          <div v-if="isAdminUser && openRegisters && openRegisters.length > 0" class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-600 mb-1.5 flex items-center justify-between">
              <span>Caja a Descontar el Retiro</span>
              <span v-if="openRegisters.length > 1" class="text-[10px] text-teal-700 font-bold bg-teal-50 px-2 py-0.5 rounded border border-teal-200 uppercase tracking-wider">
                Inter-tiendas
              </span>
            </label>
            <div class="relative">
              <select
                v-model="form.cash_register_id"
                class="w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-800 bg-white cursor-pointer transition-colors"
                style="border: 1.5px solid #d1d5db; outline: none;"
                required
              >
                <option v-for="caja in openRegisters" :key="caja.id" :value="caja.id">
                  {{ caja.name }} — {{ caja.organization_name }} ({{ caja.opened_by_name }})
                </option>
              </select>
            </div>
            <p v-if="openRegisters.length > 1" class="text-[11px] text-gray-500 mt-1">
              Seleccione la caja abierta (propia o de otra sucursal) de donde se toma físicamente el dinero.
            </p>
          </div>

          <div v-else-if="isAdminUser && (!openRegisters || openRegisters.length === 0)" class="mb-4 p-3 rounded-lg text-xs font-medium flex items-center gap-2" style="background-color: #fffbeb; border: 1.5px solid #fde68a; color: #92400e;">
            <i class="fa fa-exclamation-triangle"></i>
            <span>No hay cajas abiertas actualmente en el sistema para debitar retiros.</span>
          </div>
          
          <!-- Moneda del Retiro -->
          <div class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-600 mb-1.5">Moneda del Retiro</label>
            <div class="grid grid-cols-3 gap-2">
              <button
                type="button"
                @click="form.currency = 'cop'"
                class="py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
                :style="form.currency === 'cop' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #f3f4f6; color: #4b5563; border: 1px solid #e5e7eb;'"
              >
                <i class="fa fa-coins mr-1"></i>
                <span>Pesos (COP)</span>
              </button>
              <button
                type="button"
                @click="form.currency = 'usd'"
                class="py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
                :style="form.currency === 'usd' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #f3f4f6; color: #4b5563; border: 1px solid #e5e7eb;'"
              >
                <i class="fa fa-dollar-sign mr-1"></i>
                <span>Dólares ($)</span>
              </button>
              <button
                type="button"
                @click="form.currency = 'bs'"
                class="py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
                :style="form.currency === 'bs' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #f3f4f6; color: #4b5563; border: 1px solid #e5e7eb;'"
              >
                <i class="fa fa-money-bill-wave mr-1"></i>
                <span>Bolívares (Bs)</span>
              </button>
            </div>
          </div>

          <!-- Monto -->
          <div class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-600 mb-1.5">
              Monto a Retirar ({{ form.currency.toUpperCase() }})
            </label>
            <div class="relative">
              <span class="absolute left-3 top-2.5 font-bold text-gray-500">
                {{ form.currency === 'usd' ? '$' : (form.currency === 'bs' ? 'Bs.' : 'COP') }}
              </span>
              <input
                type="number"
                step="any"
                min="0.01"
                v-model.number="form.amount"
                required
                class="w-full pl-14 pr-4 py-2.5 rounded-lg font-extrabold text-lg text-gray-800"
                style="border: 1.5px solid #d1d5db; outline: none;"
                placeholder="0.00"
              />
            </div>
          </div>

          <!-- Concepto / Motivo -->
          <div class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-600 mb-1.5">Motivo / Concepto del Retiro</label>
            <input
              type="text"
              v-model="form.description"
              required
              class="w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-800"
              style="border: 1.5px solid #d1d5db; outline: none;"
              placeholder="Ej. Pago a proveedor de pan, gasto operativo, caja fuerte..."
            />
          </div>

          <!-- Clave de Administrador -->
          <div class="mb-5 p-3.5 rounded-xl" style="background-color: #fef2f2; border: 1.5px solid #fecaca;">
            <label class="block text-xs font-bold uppercase text-red-800 mb-1 flex items-center">
              <i class="fa fa-lock mr-1.5"></i> Clave de Autorización (Administrador)
            </label>
            <input
              type="password"
              v-model="form.admin_pin"
              required
              class="w-full px-3 py-2 rounded-lg text-sm bg-white font-bold tracking-widest text-gray-800"
              style="border: 1.5px solid #fca5a5; outline: none;"
              placeholder="Ingrese clave o PIN de admin"
            />
            <p class="text-[11px] text-red-600 mt-1">Solo los administradores autorizados poseen esta clave.</p>
          </div>

          <!-- Botones -->
          <div class="flex items-center justify-between pt-2 border-t border-gray-100">
            <button
              type="button"
              @click="close"
              class="px-4 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-5 py-2.5 text-xs md:text-sm font-bold text-white rounded-lg transition-all shadow-md flex items-center space-x-1.5 cursor-pointer"
              style="background-color: #dc2626;"
            >
              <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
              <i v-else class="fa fa-check-circle mr-1"></i>
              <span>Autorizar y Descontar Retiro</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Tab 2: Configurar Clave de Retiros (Solo Admin) -->
      <div v-if="tab === 'pin_config'" class="p-6">
        <form @submit.prevent="submitPinChange">
          <div class="p-3 mb-4 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
            Esta clave o PIN es la que solicitará el sistema a los cajeros para autorizar cualquier retiro de dinero en efectivo.
          </div>

          <div class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-700 mb-1.5">Nueva Clave de Retiros (PIN)</label>
            <input
              type="password"
              v-model="pinForm.withdrawal_pin"
              required
              minlength="4"
              maxlength="20"
              class="w-full px-3 py-2.5 rounded-lg text-sm font-bold tracking-wider text-gray-800"
              style="border: 1.5px solid #d1d5db; outline: none;"
              placeholder="Ej. 1234 o clave segura"
            />
          </div>

          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              type="button"
              @click="tab = 'withdrawal'"
              class="px-4 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Volver
            </button>
            <button
              type="submit"
              :disabled="loadingPin"
              class="px-5 py-2.5 text-xs font-bold text-white rounded-lg transition-all shadow-md flex items-center space-x-1.5 cursor-pointer"
              style="background-color: #00b074;"
            >
              <i v-if="loadingPin" class="fa fa-spinner fa-spin mr-1"></i>
              <i v-else class="fa fa-save mr-1"></i>
              <span>Guardar Nueva Clave</span>
            </button>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'WithdrawalModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    cashRegisterId: {
      type: [Number, String],
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      tab: 'withdrawal',
      loading: false,
      loadingPin: false,
      form: this.$inertia.form({
        amount: '',
        currency: 'cop',
        description: '',
        admin_pin: '',
        cash_register_id: this.cashRegisterId
      }),
      pinForm: this.$inertia.form({
        withdrawal_pin: ''
      })
    }
  },

  computed: {
    isAdminUser() {
      const u = this.$page.props.auth ? this.$page.props.auth.user : null
      return this.isAdmin || Boolean(u && (u.owner || u.role === 'admin' || u.role === 1))
    },
    openRegisters() {
      return this.$page.props.open_cash_registers || []
    }
  },

  watch: {
    cashRegisterId(newVal) {
      if (newVal) this.form.cash_register_id = newVal
    },
    show(val) {
      if (val) {
        if (!this.form.cash_register_id) {
          if (this.cashRegisterId) {
            this.form.cash_register_id = this.cashRegisterId
          } else if (this.$page.props.open_cash_register) {
            this.form.cash_register_id = this.$page.props.open_cash_register.id
          } else if (this.openRegisters && this.openRegisters.length > 0) {
            this.form.cash_register_id = this.openRegisters[0].id
          }
        }
      }
    }
  },

  methods: {
    close() {
      this.form.reset()
      this.pinForm.reset()
      this.tab = 'withdrawal'
      this.$emit('close')
    },

    submitWithdrawal() {
      if (this.loading) return
      this.loading = true

      this.form.post('/cash-transactions/withdrawal', {
        preserveScroll: true,
        onSuccess: (page) => {
          this.loading = false
          const flash = page?.props?.flash || this.$page?.props?.flash || {}
          if (flash.error) {
            if (this.$swal) {
              this.$swal.fire({
                icon: 'error',
                title: 'No se pudo realizar el retiro',
                text: flash.error,
                confirmButtonColor: '#dc2626'
              })
            } else {
              alert('No se pudo realizar el retiro: ' + flash.error)
            }
          } else {
            const successMsg = flash.success || 'Retiro de efectivo registrado y descontado exitosamente.'
            if (this.$swal) {
              this.$swal.fire({
                icon: 'success',
                title: '¡Retiro Exitoso!',
                text: successMsg,
                timer: 3500,
                showConfirmButton: true,
                confirmButtonColor: '#00b074'
              })
            } else {
              alert(successMsg)
            }
            this.close()
          }
        },
        onError: (errors) => {
          this.loading = false
          const msg = Object.values(errors).flat().join('\n') || 'Ocurrió un error al procesar el retiro.'
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error en el retiro',
              text: msg,
              confirmButtonColor: '#dc2626'
            })
          } else {
            alert(msg)
          }
        }
      })
    },

    submitPinChange() {
      if (this.loadingPin) return
      this.loadingPin = true

      this.pinForm.post('/organizations/withdrawal-pin', {
        preserveScroll: true,
        onSuccess: (page) => {
          this.loadingPin = false
          const flash = page?.props?.flash || this.$page?.props?.flash || {}
          if (flash.error) {
            if (this.$swal) {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: flash.error,
                confirmButtonColor: '#dc2626'
              })
            } else {
              alert(flash.error)
            }
          } else {
            const msg = flash.success || 'Clave de retiro actualizada correctamente.'
            if (this.$swal) {
              this.$swal.fire({
                icon: 'success',
                title: '¡Clave Actualizada!',
                text: msg,
                timer: 3000,
                showConfirmButton: true,
                confirmButtonColor: '#00b074'
              })
            } else {
              alert(msg)
            }
            this.tab = 'withdrawal'
          }
        },
        onError: (errors) => {
          this.loadingPin = false
          const msg = Object.values(errors).flat().join('\n') || 'Ocurrió un error al guardar la clave.'
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              confirmButtonColor: '#dc2626'
            })
          } else {
            alert(msg)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fadeIn {
  animation: fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
